import { ChevronLeft } from '../../components/ui/icons';
import { LockCountdownBadge } from '../../components/play/cycling-ui';
import { cn } from '../../lib/cn';

/** White picks-flow header — Step 1 calls or Step 2 rank.
 *  Pass `navOnly` on Step 2 when the title/body is rendered inside the card
 *  (matching the hub's CyclingDuelsHubSection structure). */
export function CyclingPicksFlowHeader({
  backLabel,
  step,
  routeLabel,
  title,
  body,
  lockAt,
  now,
  progressPct,
  progressLabel,
  progressActive = false,
  navOnly = false,
  onBack
}: {
  backLabel: string;
  step: 1 | 2;
  routeLabel?: string;
  title: string;
  body: string;
  lockAt?: number | null;
  now: number;
  progressPct?: number;
  progressLabel?: string;
  progressActive?: boolean;
  /** When true, only the nav row (back + countdown) is rendered — title/body live inside the card. */
  navOnly?: boolean;
  /** Omitted on the inline hub flow — no back affordance on step 1. */
  onBack?: () => void;
}) {
  const eyebrow =
    step === 1
      ? `Step 1 of 2${routeLabel ? ` · ${routeLabel}` : ''}`
      : 'Step 2 of 2 · Confidence';

  return (
    <>
      <div className="flex items-center justify-between pt-3 md:pt-4">
        {onBack ? (
          <button type="button" onClick={onBack} className="tap flex items-center gap-[3px] text-ink">
            <ChevronLeft width={22} height={22} strokeWidth={2.4} />
            <span className="text-[16px] font-semibold tracking-[-0.01em]">{backLabel}</span>
          </button>
        ) : (
          <div className="flex-1" />
        )}
        {lockAt != null && lockAt > now ? <LockCountdownBadge lockAt={lockAt} now={now} /> : null}
      </div>

      {!navOnly && (
        <div className="pb-0.5 pt-4">
          <div className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-ink2">{eyebrow}</div>
          <h1 className="mt-1.5 text-[28px] font-extrabold leading-[1.02] tracking-[-0.03em] text-ink">{title}</h1>
          <p className="mt-2 text-[12.5px] font-medium leading-[1.4] text-ink2">{body}</p>
          {progressPct != null && progressLabel ? (
            <div className="flex items-center gap-2.5 pt-3.5">
              <div className="h-1.5 flex-1 overflow-hidden rounded-[3px] bg-[#EBEBF0]">
                <div
                  className="h-full rounded-[3px] bg-[linear-gradient(90deg,#2EDB7B,#0BA078)] transition-[width]"
                  style={{ width: `${Math.max(2, progressPct)}%` }}
                />
              </div>
              <span
                className={cn(
                  'num shrink-0 text-xs font-extrabold',
                  progressActive ? 'text-accent-ink' : 'text-ink2'
                )}
              >
                {progressLabel}
              </span>
            </div>
          ) : null}
        </div>
      )}
    </>
  );
}
